import React from "react";

import ModelViewerEmbed from "../3d/model.viewer.embed";
import {
  ArtworkOptimizationConfig,
  ArtworkOptimizationJob,
  ArtworkOptimizationPresetId,
  ArtworkOptimizationSelection,
} from "../../types/artworkOptimization";

interface ArtworkOptimizationPanelProps {
  config: ArtworkOptimizationConfig | null;
  available: boolean;
  eligible: boolean;
  blockedReason: string | null;
  withinWorkerLimit: boolean;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  preset: ArtworkOptimizationPresetId;
  setPreset: (preset: ArtworkOptimizationPresetId) => void;
  job: ArtworkOptimizationJob | null;
  uploadPercent: number;
  error: string | null;
  working: boolean;
  active: boolean;
  draftChanged: boolean;
  hasSourceFile: boolean;
  onCancel: () => void;
  onFinalize: (selection: ArtworkOptimizationSelection) => void;
  onRerun: () => void;
}

const formatFileSize = (bytes?: number): string => {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) {
    return "unknown";
  }
  if (bytes < 1024) {
    return `${bytes.toFixed(0)} B`;
  }
  if (bytes < 1024 ** 2) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 ** 3) {
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
};

export const ArtworkOptimizationPanel = ({
  config,
  available,
  eligible,
  blockedReason,
  withinWorkerLimit,
  enabled,
  setEnabled,
  preset,
  setPreset,
  job,
  uploadPercent,
  error,
  working,
  active,
  draftChanged,
  hasSourceFile,
  onCancel,
  onFinalize,
  onRerun,
}: ArtworkOptimizationPanelProps) => {
  if (!available && !job) {
    return null;
  }

  return (
    <section className="artwork-optimization-panel mb-4" aria-label="Artwork optimization">
      {available && (
        <>
          <div className="form-check form-switch mb-2">
            <input
              id="optimize-before-upload"
              type="checkbox"
              className="form-check-input"
              checked={enabled}
              disabled={!eligible || !withinWorkerLimit || Boolean(job)}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="optimize-before-upload">Optimize before upload</label>
          </div>
          {blockedReason && <p className="text-warning small mb-0">{blockedReason}</p>}
          {!withinWorkerLimit && (
            <p className="text-warning small mb-0">This file exceeds the active optimization worker limit.</p>
          )}
          {enabled && eligible && withinWorkerLimit && (
            <div className="optimization-preset-group" role="group" aria-label="Optimization tier">
              {config?.presets.map((item) => (
                <label className="optimization-preset" key={item.id}>
                  <input
                    type="radio"
                    name="optimization-preset"
                    value={item.id}
                    checked={preset === item.id}
                    disabled={active}
                    onChange={() => setPreset(item.id)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          )}
        </>
      )}
      {error && <p className="text-danger small mt-2 mb-0" role="alert">{error}</p>}
      {job && (
        <div className="optimization-job mt-3" aria-live="polite">
          {job.status === "awaiting_upload" && (
            working ? (
              <>
                <div className="optimization-status-row"><span>Uploading source</span><span>{uploadPercent}%</span></div>
                <progress aria-label="Source upload progress" max={100} value={uploadPercent} />
              </>
            ) : (
              <p className="text-warning small mb-0">Source upload was interrupted. Open Pending Optimizations to retry it.</p>
            )
          )}
          {["queued", "processing"].includes(job.status) && (
            <>
              <div className="optimization-status-row">
                <span>{job.progress?.message || (job.status === "queued" ? "Queued" : "Optimizing model")}</span>
                <span>{job.progress?.percent || 0}%</span>
              </div>
              <progress aria-label="Optimization progress" max={100} value={job.progress?.percent || 0} />
            </>
          )}
          {job.status === "failed" && (
            <p className="text-danger small mb-0">{job.failureReason || "Optimization failed. The original file has not been published."}</p>
          )}
          {job.status === "ready" && (
            <>
              {job.previewUrl && <ModelViewerEmbed modelUrl={job.previewUrl} className="optimization-preview" />}
              {job.metrics && (
                <p className="optimization-metrics small mb-2">
                  Source: {formatFileSize(job.metrics.sourceBytes)}. Optimized: {formatFileSize(job.metrics.optimizedBytes)}.
                </p>
              )}
              {job.warnings?.map((warning) => <p className="text-warning small mb-1" key={warning}>{warning}</p>)}
              {draftChanged && (
                <p className="text-warning small mb-2">Artwork details changed. Rerun optimization before publishing.</p>
              )}
              <div className="optimization-actions">
                <button type="button" className="btn btn-primary" disabled={working || draftChanged} onClick={() => onFinalize("optimized")}>Use optimized</button>
                <button type="button" className="btn btn-outline-secondary" disabled={working || draftChanged} onClick={() => onFinalize("original")}>Use original</button>
                {hasSourceFile && <button type="button" className="btn btn-outline-secondary" disabled={working} onClick={onRerun}>Rerun optimization</button>}
              </div>
            </>
          )}
          {active && <button type="button" className="btn btn-link px-0 mt-2" onClick={onCancel}>Cancel optimization</button>}
        </div>
      )}
    </section>
  );
};
